import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { useIsMobile } from '@worldcoo/ui/hooks/use-mobile';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useState } from 'react';
import { DONOR_APP_URL } from './external-links';
import { LanguageFlag, LANGUAGES } from './languages';
import { useNavLinks } from './nav';
import { NavStroke } from './primitives';
import { MobileMenu } from './mobile-menu';

const XL_BREAKPOINT = 1280;

/**
 * A native `<select>` (the OS picker on a phone) stretched at zero opacity over the designed trigger,
 * since an `<option>` cannot hold the flag. `opacity-0`, not `sr-only`: an unlaid-out select can't be clicked.
 */
function LanguageSelector() {
	const { t } = useLingui();
	const { language, changeLanguage } = useTranslation();
	const current = LANGUAGES.find((l) => l.code === language) ?? LANGUAGES[0];
	return (
		<div className="text-brand-nav relative flex items-center gap-1.5 rounded-full px-1 py-1">
			<LanguageFlag code={current.code} className="size-5 shrink-0" />
			<span className="font-medium">{current.short}</span>
			<ChevronDown className="size-4" />
			<select
				aria-label={t`Language`}
				value={current.code}
				onChange={(event) => {
					const next = LANGUAGES.find((lang) => lang.code === event.target.value);
					if (next) changeLanguage(next.code);
				}}
				className="absolute inset-0 cursor-pointer opacity-0"
			>
				{LANGUAGES.map((lang) => (
					<option key={lang.code} value={lang.code}>
						{lang.label}
					</option>
				))}
			</select>
		</div>
	);
}

const NAV_ITEM = 'group px-2 py-1 font-medium';

/** Current page and hover sign the word with a stroke: docs/notes/portals/hand-drawn-strokes.md */
function NavLabel(props: { label: string }) {
	return (
		<span className="relative inline-block">
			{props.label}
			<NavStroke
				className="opacity-50 group-aria-[current=page]:opacity-100"
				penClassName="transition-[stroke-dashoffset] duration-500 ease-out group-hover:[stroke-dashoffset:0] group-aria-[current=page]:[stroke-dashoffset:0] motion-reduce:transition-none"
			/>
		</span>
	);
}

export function Header() {
	const { t } = useLingui();
	const { language, changeLanguage } = useTranslation();
	const isMobile = useIsMobile(XL_BREAKPOINT);
	const navLinks = useNavLinks();
	const [menuOpen, setMenuOpen] = useState(false);

	return (
		<header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md">
			<div className="relative mx-auto flex h-[95px] max-w-[1440px] items-center px-6 xl:px-10">
				<Link to="/www" activeOptions={{ exact: true }} className="shrink-0">
					<span className="sr-only">{t`Worldcoo home`}</span>
					<WorldcooLogo className="h-11 w-auto" accentClassName="text-brand" />
				</Link>

				{/* Centred on the page from 1400px; below, Spanish and Catalan overlap the buttons, so it centres between them and the logo (TEC-4011). */}
				<nav className="hidden flex-1 items-center justify-center gap-6 min-[1400px]:absolute min-[1400px]:left-1/2 min-[1400px]:-translate-x-1/2 xl:flex">
					{navLinks.map((item) => (
						<Link key={item.to} to={item.to} className={NAV_ITEM}>
							<NavLabel label={item.label} />
						</Link>
					))}
					<a href={DONOR_APP_URL} target="_blank" rel="noopener noreferrer" className={NAV_ITEM}>
						<NavLabel label={t`Certify donation`} />
					</a>
				</nav>

				<div className="ml-auto hidden items-center gap-4 xl:flex">
					<LanguageSelector />
					<Link
						to="/www/contact"
						className="bg-brand hover:bg-brand-strong flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-4 font-medium text-black transition-colors"
					>
						{t`Contact us`}
						<span className="bg-ink grid size-6 place-items-center rounded-full text-white">
							<ArrowUpRight className="size-4" />
						</span>
					</Link>
				</div>

				{/* Mounted outright, not lazily on tap: ~2KB brotli over vendors the first paint already carries. */}
				{isMobile && (
					<MobileMenu language={language} changeLanguage={changeLanguage} open={menuOpen} onOpenChange={setMenuOpen} />
				)}
			</div>
		</header>
	);
}
